import { fmtDate, fmtTime } from '@/lib/dates.js';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Info, ScrollText } from 'lucide-react';
import { compliance, ApiError, type AuditRow } from '@/api/client';
import { humanActionLabel, humanRole, humanDetails } from '@/lib/humanise';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';


/**
 * Sprint-2 right-rail panel used by the DREAM-18 wizard and DREAM-19 upload
 * card. Polls the existing /compliance/audit-trail endpoint (Sprint-1) with
 * scope filters, refreshes every 10 s, renders the latest 25 rows in
 * chronological-descending order.
 *
 * Renders nothing if no scope is supplied. All values pass through
 * humanise.ts (no raw codes / UUIDs surface to the user).
 */

export interface AuditTrailPanelProps {
  /** At least one scope is required, else the panel returns null. */
  scope: {
    studyId?: string;
    studySiteId?: string;
    siteId?: string;
    subjectId?: string;
    /** Filter to a specific set of action codes (e.g. ['study.onboarding.step_completed']). */
    actions?: string[];
  };
  /** Override the default 10 s polling interval. */
  refetchIntervalMs?: number;
  /** Override the row cap (default 25). */
  limit?: number;
}

export function AuditTrailPanel({
  scope,
  refetchIntervalMs = 10_000,
  limit = 25,
}: AuditTrailPanelProps) {
  const hasScope = !!(scope.studyId || scope.studySiteId || scope.siteId || scope.subjectId);

  // The Sprint-1 /compliance/audit-trail endpoint is site-scoped by JWT.
  // We narrow further with explicit filters where supported and otherwise
  // filter client-side.
  const q = useQuery({
    queryKey: ['audit-trail-panel', scope, limit],
    queryFn: () =>
      compliance.listAuditTrail({
        study_site_id: scope.studySiteId,
        study_id: scope.studyId,
        // Pass subjectId as resource_id so the backend filters audit rows
        // where resource_id = subjectId (covers screening, demographics,
        // eligibility, enrollment). Consent events use consent_record_id as
        // resource_id — those appear separately in the consent records panel.
        resource_id: scope.subjectId,
        action: scope.actions && scope.actions.length > 0 ? scope.actions : undefined,
        limit,
        page: 1,
      }),
    enabled: hasScope,
    refetchInterval: refetchIntervalMs,
    staleTime: refetchIntervalMs / 2,
  });

  if (!hasScope) return null;

  // A role that may not read this trail sees NO PANEL, not a red box telling
  // them so (house rule, 2026-10-09: hide the affordance, never fail on
  // click). The whole panel is the affordance here — a permission boundary
  // rendered as an error reads as a broken screen, which is exactly how this
  // one was reported.
  //
  // Only 403 is swallowed. A network blip or a 500 is a real failure the user
  // should see, and hiding those would turn an outage into "the audit trail is
  // empty", which is worse in a Part 11 app than showing an error.
  const forbidden = q.error instanceof ApiError && q.error.status === 403;
  if (forbidden) return null;

  const rows = (q.data?.rows ?? []).filter((r) => {
    if (scope.studyId && r.study_id && r.study_id !== scope.studyId) return false;
    return true;
  });

  return (
    <div className="flex flex-1 flex-col min-h-0">
      <header className="flex items-center gap-2 border-b border-surface-border px-4 py-3">
        <ScrollText className="h-4 w-4 text-ink-muted" />
        <h3 className="text-sm font-semibold text-ink-primary">Audit Trail</h3>
        <span className="text-[10px] uppercase tracking-wider text-ink-muted">live</span>
      </header>
      <div className="flex-1 overflow-y-auto p-3">
        {q.isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="animate-pulse overflow-hidden rounded-md border border-surface-border bg-white">
                <div className="flex">
                  <div className="w-1 shrink-0 bg-surface-muted" />
                  <div className="flex-1 space-y-1.5 p-2.5">
                    <div className="h-3 w-3/4 rounded bg-surface-muted" />
                    <div className="h-2.5 w-1/2 rounded bg-surface-muted/70" />
                    <div className="h-2.5 w-1/3 rounded bg-surface-muted/60" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {q.isError && !forbidden && (
          <div className={`flex items-start gap-2 rounded-md p-3 text-xs ${errorToneClass(q.error)}`}>
            {isPermissionError(q.error) ? <Info className="mt-0.5 h-3.5 w-3.5 shrink-0"/> : <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
            <span>{errorText(q.error, 'Failed to load audit trail.')}</span>
          </div>
        )}
        {q.data && rows.length === 0 && (
          <div className="py-6 text-center text-xs text-ink-muted">No activity yet.</div>
        )}
        {rows.length > 0 && (
          <ol className="space-y-2">
            {rows.map((r) => (
              <AuditTrailEntry key={r.audit_id} row={r} />
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function AuditTrailEntry({ row }: { row: AuditRow }) {
  const details = humanDetails(
    row.action,
    row.new_value as unknown as Record<string, unknown> | null,
    {},
  );

  const dateStr = fmtDate(row.timestamp);
  const timeStr = fmtTime(row.timestamp);

  return (
    <li className="rounded-md border border-surface-border bg-white overflow-hidden">
      {/* Coloured left accent bar */}
      <div className="flex">
        <div className="w-1 shrink-0 bg-navy-100" />
        <div className="flex-1 p-2.5 min-w-0">
          {/* Action + timestamp */}
          <div className="flex items-start justify-between gap-2 mb-1">
            <span className="text-[11px] font-semibold text-ink-primary leading-tight">
              {humanActionLabel(row.action, row.new_value as Record<string, unknown> | null)}
            </span>
          </div>
          {/* Date · Time */}
          <div className="flex items-center gap-1 text-[10px] text-ink-muted mb-1.5">
            <span>{dateStr}</span>
            <span className="text-ink-muted/50">·</span>
            <span className="font-medium text-ink-secondary">{timeStr}</span>
          </div>
          {/* Actor */}
          {(row.user_full_name || row.user_role_at_time) && (
            <div className="text-[10px] text-ink-muted">
              By:{' '}
              <span className="font-medium text-ink-secondary">
                {row.user_full_name ?? humanRole(row.user_role_at_time!)}
              </span>
              {row.user_full_name && row.user_role_at_time && (
                <span> · {humanRole(row.user_role_at_time)}</span>
              )}
            </div>
          )}
          {/* Detail line */}
          {details && (
            <div className="mt-1.5 text-[10px] text-ink-muted leading-relaxed border-t border-surface-border pt-1.5">
              {details}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
